import React from 'react';
import {observer} from 'mobx-react';
import debounce from 'lodash/debounce';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import classNames from 'classnames';
import {requireImage} from '../../../../utils/utils';
import {ObservationForm} from '../ObservationForm';
import {TsVideoSubscriber} from '../../../../components/ts-video-subscriber/TsVideoSubscriber';
import {TsImageEditor} from '../../../../components/ts-image-editor/ts-image-editor';
import {CameraZoomControl} from '@techsee/techsee-ui-common/lib/camera-zoom-control';
import {RecordingIndicator} from '../../../../components/recording-indicator/component';

import './MainMiniCommon.scss';
import {MessageAlert} from '@techsee/techsee-ui-common/lib/message-alert';
import {Toast} from '../../../../components/toast/Toast';

export interface IMainMiniCommonProps {
    main: any;
    containerClassName?: string;
}

@observer
export class MainMiniCommon extends TechseeBaseComponent<IMainMiniCommonProps> {
    private _handleMiniZoomChange = debounce((level: number) => {
        const {main} = this.props;
        main?.dashboardState?.cameraZoomController?.requestZoom(main?.chatApi, level);
    }, 200);

    componentWillUnmount(): void {
        this._handleMiniZoomChange.cancel();
        const {main} = this.props;
        main?.dashboardState?.cameraZoomController?.clearPendingZoom();
    }

    renderInternal(): JSX.Element {
        const {main} = this.props;
        const dashboardState = main?.dashboardState;

        if (!dashboardState?.dashboardControllerLoaded) {
            return <React.Fragment />;
        }

        const isVideoMode = main.isVideoOrScreensharingMode?.(dashboardState.clientRoomStorage.mode);
        const networkStatus = main.getNetworkStatus?.();
        const screenshots = [...(dashboardState.screenshots || [])].reverse();

        const isSubscriberVisible =
            isVideoMode &&
            !dashboardState.videoModeState.videoState.isPaused &&
            !dashboardState.isMiniDashboardLoading &&
            dashboardState.videoModeState.videoState.focus;

        return (
            <div className={classNames(this.props.containerClassName, {'is-mini-mobile': main.isMobile})}>
                <div className={classNames('ts-mini-dashboard-main-drawer', {open: dashboardState.drawerOpen})}>
                    <div className='ts-mini-dashboard-main-drawer-content'>
                        <div className='ts-mini-dashboard-content'>
                            <div className='ts-mini-dashboard-controls'>
                                <div className='ts-mini-dashboard-session-buttons-top'>
                                    <div className='ts-mini-dashboard-menu-button transparent'>
                                        <a
                                            title={main.translate(
                                                `DASHBOARD_MINI.TOOLTIP.NETWORK_STATUS_${networkStatus}`
                                            )}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage(
                                                    networkStatus === main.connectionTypes?.WIFI
                                                        ? 'mini-dashboard/wifi.png'
                                                        : 'mini-dashboard/network-connected.png'
                                                )}
                                                alt=''
                                            />
                                        </a>
                                    </div>

                                    {!dashboardState.videoModeState.videoState.focus && (
                                        <div className='ts-meeting-mode-icons'>
                                            {!main.observe && dashboardState.currentResource?.url && (
                                                <div
                                                    className='ts-mini-dashboard-menu-button'
                                                    style={{
                                                        backgroundColor: main.getActiveColorName(),
                                                        position: 'relative'
                                                    }}>
                                                    <a
                                                        className='main-text-color'
                                                        onClick={() => main.openMiniMenuDropdownOpen()}>
                                                        <img
                                                            className='ts-menu-icon ts-annotations-dynamic-icon'
                                                            src={requireImage(
                                                                `mini-dashboard/${dashboardState.miniDashboardActiveTool || 'drag'}.png`
                                                            )}
                                                            alt=''
                                                        />
                                                    </a>
                                                    {dashboardState.miniMenuDropdownOpen && (
                                                        <div className='dropdown-menu' style={{display: 'block'}}>
                                                            <a
                                                                className='close-button'
                                                                onClick={() => main.closeCloseMenuDropdown()}>
                                                                <img
                                                                    className='ts-menu-icon'
                                                                    src={requireImage('mini-dashboard/close.png')}
                                                                    alt=''
                                                                />
                                                            </a>
                                                            <h5 className='ts-toolbox-title'>
                                                                {main.translate(
                                                                    'DASHBOARD_MINI.VIEW.ANNOTATION_TOOLBOX'
                                                                )}
                                                            </h5>
                                                            <div className='ts-mini-dashboard-annotations'>
                                                                {['pen', 'arrow', 'circle', 'rectangle'].map((tool) => (
                                                                    <a
                                                                        key={tool}
                                                                        className={classNames({
                                                                            selected: main.isActiveTool(tool)
                                                                        })}
                                                                        onClick={() => main.selectTool(tool)}>
                                                                        <img
                                                                            src={requireImage(
                                                                                `mini-dashboard/${tool}.png`
                                                                            )}
                                                                            alt=''
                                                                        />
                                                                    </a>
                                                                ))}
                                                                {main.isSupportTextAnnotation && (
                                                                    <a
                                                                        className={classNames({
                                                                            selected: main.isActiveTool('text')
                                                                        })}
                                                                        onClick={() => main.selectTool('text')}>
                                                                        <img
                                                                            src={requireImage(
                                                                                'mini-dashboard/text.png'
                                                                            )}
                                                                            alt=''
                                                                        />
                                                                    </a>
                                                                )}
                                                                <a
                                                                    className={classNames({
                                                                        selected: main.isActiveTool('hand')
                                                                    })}
                                                                    onClick={() => main.selectTool('drag')}>
                                                                    <img
                                                                        src={requireImage('mini-dashboard/drag.png')}
                                                                        alt=''
                                                                    />
                                                                </a>
                                                            </div>
                                                            <h5 className='ts-toolbox-title'>
                                                                {main.translate('DASHBOARD_MINI.VIEW.TOOLBOX_COLOR')}
                                                            </h5>
                                                            <div className='ts-mini-dashboard-colors'>
                                                                {[9, 6, 5, 12].map((idx) => (
                                                                    <a
                                                                        key={idx}
                                                                        className={classNames({
                                                                            selected: main.isColorSelected(
                                                                                main.colors[idx].hexCode
                                                                            )
                                                                        })}
                                                                        onClick={() =>
                                                                            main.setColor(main.colors[idx].hexCode)
                                                                        }>
                                                                        <div
                                                                            className={classNames('color-block', {
                                                                                'ts-mini-dashboard-color-picker-border':
                                                                                    idx === 6
                                                                            })}
                                                                            style={{
                                                                                background: main.colors[idx].hexCode
                                                                            }}
                                                                        />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {!main.observe && dashboardState.currentResource?.url && (
                                                <a
                                                    className={classNames('ts-mini-dashboard-menu-button', {
                                                        'disabled-mini-dashboard-button': !main.hasAnnotations()
                                                    })}
                                                    onClick={() => main.undo()}>
                                                    <img
                                                        className='ts-menu-icon'
                                                        src={requireImage('mini-dashboard/undo.png')}
                                                        alt=''
                                                    />
                                                </a>
                                            )}

                                            {(dashboardState.clientRoomStorage.mode !== main.MeetingMode.video ||
                                                !dashboardState.videoModeState.videoState.focus) &&
                                                dashboardState.currentResource?.url &&
                                                main.displaySendButton && (
                                                    <a
                                                        className='ts-mini-dashboard-menu-button ts-mini-dashboard-menu-send'
                                                        onClick={() => main.sendImage()}>
                                                        <img
                                                            className='ts-menu-icon'
                                                            src={requireImage('mini-dashboard/send.png')}
                                                            alt=''
                                                        />
                                                    </a>
                                                )}
                                        </div>
                                    )}

                                    {!main.observe && isVideoMode && dashboardState.videoModeState.videoState.focus && (
                                        <a
                                            className='ts-mini-dashboard-menu-button main-text-color portrait-only'
                                            onClick={() => main.streamAction(main.CLASSIFY_EVENT_SOURCES.VIDEO_CLICKED)}
                                            title={main.translate('DASHBOARD_MINI.TOOLTIP.SNAPSHOT')}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mini-dashboard/snapshot.png')}
                                                alt=''
                                            />
                                        </a>
                                    )}
                                </div>

                                <div className='ts-mini-dashboard-session-buttons-bottom'>
                                    {dashboardState.floatingToolbarState.displayOnDemandRecordingControls && (
                                        <a
                                            className={classNames(
                                                'ts-mini-dashboard-menu-button ts-mini-dashboard-record-button',
                                                {
                                                    recording: dashboardState.isRecording,
                                                    'disabled-mini-dashboard-button':
                                                        !dashboardState.floatingToolbarState.recordButtonReady
                                                }
                                            )}
                                            onClick={() =>
                                                dashboardState.floatingToolbarState.recordButtonReady &&
                                                main.onRecordButton()
                                            }
                                            title={main.translate(
                                                dashboardState.isRecording
                                                    ? 'DASHBOARD_MINI.TOOLTIP.STOP_RECORDING'
                                                    : 'DASHBOARD_MINI.TOOLTIP.START_RECORDING'
                                            )}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage(
                                                    dashboardState.isRecording
                                                        ? 'mini-dashboard/record-stop.png'
                                                        : 'mini-dashboard/record-start.png'
                                                )}
                                                alt=''
                                            />
                                        </a>
                                    )}

                                    {dashboardState?.displayAudioButton &&
                                        (() => {
                                            const isMuted = dashboardState.videoModeState.isMicrophoneMuted;
                                            const icon = isMuted ? 'muted.png' : 'unmuted.png';
                                            return (
                                                <a
                                                    className={classNames(
                                                        'ts-mini-dashboard-menu-button orange-button',
                                                        {
                                                            'client-connecting-to-audio-stream':
                                                                !dashboardState.videoModeState.isAudioStreamConnected
                                                        }
                                                    )}
                                                    onClick={() => main.videoModeController?.toggleMute()}
                                                    title={main.audioService?.voipMicText}>
                                                    <img
                                                        className='ts-menu-icon'
                                                        src={requireImage(`mini-dashboard/${icon}`)}
                                                        alt=''
                                                    />
                                                </a>
                                            );
                                        })()}

                                    {!main.observe && dashboardState.showScanners && (
                                        <a
                                            className='ts-mini-dashboard-menu-button orange-button button-overlap-group'
                                            onClick={() => main.snapAndScan()}
                                            title={main.translate('REACT.FLOATING_TOOLBAR.SCAN')}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mini-dashboard/scan.png')}
                                                alt='scan icon'
                                            />
                                        </a>
                                    )}

                                    {!main.observe &&
                                        dashboardState.clientRoomStorage.mode !== main.MeetingMode.video &&
                                        (main.isModeAvailable(main.MeetingMode.video) ||
                                            (dashboardState.isScreenMirroring &&
                                                dashboardState.clientRoomStorage.availableModes?.includes(
                                                    main.MeetingMode.video
                                                ))) && (
                                            <a
                                                className={classNames(
                                                    'ts-mini-dashboard-menu-button orange-button button-overlap-group',
                                                    {'disabled-mini-dashboard-button': !dashboardState.modesEnabled}
                                                )}
                                                onClick={() =>
                                                    dashboardState.modesEnabled &&
                                                    main.requestMode(main.MeetingMode.video)
                                                }
                                                title={main.translate('DASHBOARD_MINI.TOOLTIP.VIDEO_MODE')}>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mini-dashboard/switch-video.png')}
                                                    alt=''
                                                />
                                            </a>
                                        )}

                                    {!main.observe &&
                                        (dashboardState.clientRoomStorage.mode === main.MeetingMode.video ||
                                            dashboardState.isScreenMirroring) &&
                                        (main.isModeAvailable(main.MeetingMode.oneClick) ||
                                            main.isModeAvailable(main.MeetingMode.images) ||
                                            (dashboardState.isScreenMirroring &&
                                                (dashboardState.clientRoomStorage.availableModes?.includes(
                                                    main.MeetingMode.oneClick
                                                ) ||
                                                    dashboardState.clientRoomStorage.availableModes?.includes(
                                                        main.MeetingMode.images
                                                    )))) && (
                                            <a
                                                className={classNames(
                                                    'ts-mini-dashboard-menu-button orange-button button-overlap-group',
                                                    {'disabled-mini-dashboard-button': !dashboardState.modesEnabled}
                                                )}
                                                onClick={() =>
                                                    dashboardState.modesEnabled && main.getImageSharingType()
                                                }
                                                title={main.translate('DASHBOARD_MINI.TOOLTIP.PHOTO_MODE')}>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mini-dashboard/switch-camera.png')}
                                                    alt=''
                                                />
                                            </a>
                                        )}

                                    {!main.observe && dashboardState.displayScreenMirroringModeButton && (
                                        <a
                                            className={classNames(
                                                'ts-mini-dashboard-menu-button orange-button button-overlap-group',
                                                {'disabled-mini-dashboard-button': !dashboardState.modesEnabled}
                                            )}
                                            onClick={() =>
                                                dashboardState.modesEnabled && main.requestMode(main.MeetingMode.screen)
                                            }
                                            title={main.translate('DASHBOARD_MINI.TOOLTIP.SCREEN_MIRRORING_MODE')}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mini-dashboard/screensharing.png')}
                                                alt=''
                                            />
                                        </a>
                                    )}

                                    {!main.observe && !dashboardState?.offlineMode && (
                                        <a
                                            className='ts-mini-dashboard-menu-button red-button'
                                            onClick={() =>
                                                main.endMeeting(false, main.LogEvents.closedByTypes.dashboard)
                                            }
                                            title={main.translate('DASHBOARD_MINI.TOOLTIP.CLOSE_SESSION')}>
                                            <div>
                                                <img
                                                    className='ts-menu-icon'
                                                    src={requireImage('mini-dashboard/end-meeting.png')}
                                                    alt=''
                                                />
                                            </div>
                                        </a>
                                    )}

                                    {main.observe && !dashboardState?.offlineMode && (
                                        <a
                                            className='ts-mini-dashboard-menu-button red-button'
                                            onClick={() => main.leaveMeeting()}>
                                            <img
                                                className='ts-menu-icon'
                                                src={requireImage('mini-dashboard/end-meeting.png')}
                                                alt=''
                                            />
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className='ts-notifications-container'>
                                <div className='ts-toast-container'>
                                    {dashboardState.messageAlert?.display && (
                                        <MessageAlert
                                            className='use-common-styles message-alert-audio'
                                            color={dashboardState.messageAlert.color}
                                            label={dashboardState.messageAlert.label}
                                        />
                                    )}
                                    {dashboardState?.lastEvent?.text && (
                                        <Toast message={dashboardState.lastEvent.text} duration={2500} />
                                    )}
                                </div>
                            </div>

                            {main.enableObservation && <ObservationForm main={main} />}

                            <div
                                className={classNames('right-section-container', {
                                    'desktop-sharing-width': dashboardState?.isDesktopSharingSession
                                })}>
                                <div className='ts-mini-dashboard-canvas-area dashboard-main-content-container'>
                                    {dashboardState.isMiniDashboardLoading && (
                                        <div className='mini-dashboard-loader'>{main.loaderText}</div>
                                    )}

                                    {/* Video Subscriber - kept mounted to avoid iOS duplicate video race condition */}
                                    {main.tsVideoSubscriberController && (
                                        <div
                                            className={classNames('ts-video-subscriber-container', {
                                                'pointer-disabled': main.isClientPointerUnavailable
                                            })}
                                            style={isSubscriberVisible ? {position: 'relative'} : {display: 'none'}}>
                                            <TsVideoSubscriber
                                                controller={main.tsVideoSubscriberController}
                                                className='ts-video-subscriber-mini-dashboard'
                                                detachable={{
                                                    enabled: true,
                                                    condition: !dashboardState.videoModeState.videoState.focus,
                                                    targetSelector: '.ts-detached-video-target',
                                                    position: 'append'
                                                }}
                                            />
                                            {dashboardState.floatingToolbarState.displayOnDemandRecordingControls &&
                                                dashboardState.isRecording && (
                                                    <RecordingIndicator
                                                        pill
                                                        label={main.translate(
                                                            'DASHBOARD_MINI.VIEW.RECORDING_INDICATOR.TITLE'
                                                        )}
                                                    />
                                                )}

                                            {dashboardState.cameraZoomController.displayCameraZoomControl && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        right: 6,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        zIndex: 10
                                                    }}>
                                                    <CameraZoomControl
                                                        min={dashboardState.clientRoomStorage.zoomCapability!.min ?? 1}
                                                        max={dashboardState.clientRoomStorage.zoomCapability!.max ?? 5}
                                                        step={
                                                            dashboardState.clientRoomStorage.zoomCapability!.step ?? 0.1
                                                        }
                                                        value={dashboardState.clientRoomStorage.zoomLevel}
                                                        disabled={
                                                            dashboardState.cameraZoomController.cameraZoomDisabled ||
                                                            dashboardState.cameraZoomController.isZoomPending
                                                        }
                                                        onChange={this._handleMiniZoomChange}
                                                        onReset={() => {
                                                            this._handleMiniZoomChange.cancel();
                                                            dashboardState.cameraZoomController.requestZoom(
                                                                main?.chatApi,
                                                                1
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Image Editor */}
                                    {!dashboardState.videoModeState.videoState.focus &&
                                        dashboardState.currentResource &&
                                        !dashboardState.videoControlState.isActive &&
                                        main.imageEditorController && (
                                            <div
                                                className='ts-mini-dashboard-image-container image-editor-container'
                                                onMouseDown={() => main.closeMiniMenuDropdown()}>
                                                <TsImageEditor controller={main.imageEditorController} />
                                            </div>
                                        )}

                                    {dashboardState.clientRoomStorage.mode === main.MeetingMode.images &&
                                        !dashboardState.currentResource?.url && (
                                            <div className='ts-mini-dashboard-initial-screen'>
                                                {dashboardState.clientRoomStorage.mode === main.MeetingMode.images && (
                                                    <div>
                                                        <h4>{main.translate('DASHBOARD_MINI.VIEW.INSTRUCTION_ASK')}</h4>
                                                        <ol>
                                                            <li>
                                                                {main.translate('DASHBOARD_MINI.VIEW.INSTRUCTION_TAP')}
                                                            </li>
                                                            <li>
                                                                {main.translate(
                                                                    'DASHBOARD_MINI.VIEW.INSTRUCTION_TAKE_PHOTO'
                                                                )}
                                                            </li>
                                                            <li>
                                                                {main.translate(
                                                                    'DASHBOARD_MINI.VIEW.INSTRUCTION_PROBLEM_AREA'
                                                                )}
                                                            </li>
                                                            <li>
                                                                {main.translate(
                                                                    'DASHBOARD_MINI.VIEW.INSTRUCTION_USE_PHOTO'
                                                                )}
                                                            </li>
                                                        </ol>
                                                    </div>
                                                )}
                                                {!dashboardState.clientRoomStorage.connected && (
                                                    <div>
                                                        <h6>{main.translate('DASHBOARD.VIEW.WAITING_FOR_CUSTOMER')}</h6>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                </div>

                                <div className='ts-mini-dashboard-img-strip-container'>
                                    <div
                                        className='ts-detached-video-target'
                                        style={{
                                            display:
                                                main.isVideoOrScreensharingMode?.(
                                                    dashboardState.clientRoomStorage.mode
                                                ) && !dashboardState.videoModeState.videoState.focus
                                                    ? ''
                                                    : 'none'
                                        }}
                                        onClick={() => main.preVideoClick()}>
                                        <div
                                            className='badge-minimize-video use-common-styles'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                main.preVideoClick();
                                            }}>
                                            <span>{main.liveVideoText}</span>
                                        </div>
                                    </div>
                                    <ul className='ts-mini-dashboard-img-strip'>
                                        {((main.isMobile && screenshots.length > 0) || !main.isMobile) && (
                                            <div
                                                className={classNames('ts-gallery-title', {
                                                    offset:
                                                        isVideoMode &&
                                                        !dashboardState.videoModeState.videoState.focus &&
                                                        main.isMobile
                                                })}>
                                                {main.translate('DASHBOARD_MINI.VIEW.GALLERY')}
                                            </div>
                                        )}

                                        {screenshots.length === 0 && (
                                            <div className='no-images-container'>
                                                <div className='no-images'>
                                                    <img src={requireImage('mini-dashboard/no-images.png')} alt='' />
                                                </div>
                                                <span>{main.translate('DASHBOARD_MINI.VIEW.NO_IMAGES')}</span>
                                            </div>
                                        )}

                                        {screenshots.map((image, index) => (
                                            <li
                                                key={index}
                                                className='ts-mini-dashboard-meeting-container'
                                                onClick={() => main.selectClickedResource(image)}>
                                                <div
                                                    className={classNames('ts-mini-dashboard-meeting', {
                                                        active:
                                                            !dashboardState.videoModeState.videoState.focus &&
                                                            main.isActiveResource(image)
                                                    })}
                                                    style={{backgroundImage: `url(${image.url})`}}>
                                                    {image.scanned && (
                                                        <img
                                                            className='scanned-image'
                                                            src={requireImage('mini-dashboard/scanned.png')}
                                                            alt=''
                                                        />
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='audio-subscriber-container-client' />
                <div className='audio-subscriber-container-observer' />
                <div className='audio-subscriber-container-agent' />
            </div>
        );
    }
}
